import jwtDecode from 'jwt-decode';
import config from './config';
import { get } from 'lodash';
import { PAGES_BASE_PATHNAMES } from './consts';
import { captureError } from './error-handling';
import { SessionStorageKeys, storeOnSessionAndRedirect } from './session-storage';
import { computeChallenge, generateVerifier } from './pkce';
import { OktaScopes, OktaScopesConfig } from './okta-auth-api.consts';
import { ACCESS_TOKEN_COOKIE, REFRESH_TOKEN_COOKIE } from './authentication';
import { OktaScopeConfig } from './okta-auth-api.types';

const AUTHORIZE_REQUEST_TIMEOUT_DURATION_MS: number = 0;

export const submitOkta = async (
  username: string,
  password: string,
  requestedOktaScope: OktaScopes
): Promise<any> => {
  const oktaScopeConfig: OktaScopeConfig = OktaScopesConfig[requestedOktaScope];

  if (!oktaScopeConfig) {
    return {};
  }

  let response, data;

  try {
    response = await fetch(`${config.OKTA_DOMAIN}/api/v1/authn`, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
        options: {
          multiOptionalFactorEnroll: false,
          warnBeforePasswordExpired: false,
        },
      }),
    });
    data = await response.json();
  } catch (err) {
    captureError(err);
  }

  if (!data) {
    return {};
  }

  // Handle MFA required status
  if (data.status === 'MFA_REQUIRED') {
    // Store stateToken for MFA verification
    sessionStorage.setItem(SessionStorageKeys.OKTA_STATE_TOKEN, data.stateToken);
    return data;
  }

  if (!data.sessionToken) {
    if (data) {
      captureError(data);
    }
    return data || {};
  }

  const timeStamp: string = new Date().getTime().toString();

  const codeVerifier: string = generateVerifier();
  sessionStorage.setItem(SessionStorageKeys.OKTA_CODE_VERIFIER, codeVerifier);
  const codeChallenge: string = await computeChallenge(codeVerifier);

  const redirectUrl: string = `${config.OKTA_DOMAIN}/oauth2/${
    config.OKTA_AUTH_SERVER_ID
  }/v1/authorize?code_challenge=${codeChallenge}&code_challenge_method=S256&client_id=${
    config.OKTA_CLIENT_ID
  }&response_type=code&response_mode=fragment&scope=${oktaScopeConfig.value}offline_access%20openid&redirect_uri=${
    window.location.origin + PAGES_BASE_PATHNAMES.Login
  }&state=${timeStamp}&nonce=${timeStamp}&sessionToken=${data.sessionToken}&prompt=none`;

  setTimeout(() => {
    window.location.replace(redirectUrl);
  }, AUTHORIZE_REQUEST_TIMEOUT_DURATION_MS);
};

export const generateTokensOkta = async (code: string): Promise<any> => {
  let response, data;

  const codeVerifier: string = sessionStorage.getItem(SessionStorageKeys.OKTA_CODE_VERIFIER);

  if (!code || !codeVerifier) {
    return {};
  }

  try {
    response = await fetch(`${config.OKTA_DOMAIN}/oauth2/${config.OKTA_AUTH_SERVER_ID}/v1/token`, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `client_id=${config.OKTA_CLIENT_ID}&code_verifier=${codeVerifier}&redirect_uri=${
        window.location.origin + PAGES_BASE_PATHNAMES.Login
      }&grant_type=authorization_code&code=${code}`,
    });
    data = await response.json();
    sessionStorage.removeItem(SessionStorageKeys.OKTA_CODE_VERIFIER);
  } catch (err) {
    captureError(err);
  }

  const appliedObj = {
    [ACCESS_TOKEN_COOKIE]: get(data, 'access_token'),
    [REFRESH_TOKEN_COOKIE]: get(data, 'refresh_token'),
  };

  const redirectMiddleware = (redirectUrl: string) =>
    `${config.OKTA_DOMAIN}/oauth2/${config.OKTA_AUTH_SERVER_ID}/v1/logout?id_token_hint=${get(
      data,
      'id_token'
    )}&post_logout_redirect_uri=${redirectUrl}`;

  storeOnSessionAndRedirect(appliedObj, redirectMiddleware);
};

export const refreshTokensOkta = async (refreshToken: string): Promise<any> => {
  if (!refreshToken) {
    return {};
  }

  let response, data;

  try {
    response = await fetch(`${config.OKTA_DOMAIN}/oauth2/${config.OKTA_AUTH_SERVER_ID}/v1/token`, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `client_id=${config.OKTA_CLIENT_ID}&grant_type=refresh_token&refresh_token=${refreshToken}`,
    });
    
    // For 400 status code, try to parse the response to get specific error details
    if (response.status === 400) {
      try {
        // Clone the response before reading it as JSON
        const errorResponse = await response.clone().json();
        
        // Log the exact error message from Okta
        if (errorResponse.error_description) {
          console.error(`Token refresh failed: ${errorResponse.error_description}`);
        } else if (errorResponse.error) {
          console.error(`Token refresh failed: ${errorResponse.error}`);
        } else {
          console.error(`Token refresh failed with status 400`);
        }
        
        // Capture the error for monitoring
        captureError(new Error(`Okta token refresh error: ${errorResponse.error_description || errorResponse.error || 'Status 400'}`));
      } catch (parseError) {
        console.error('Token refresh failed with 400 status - could not parse error details');
        captureError(parseError);
      }
      // Return empty object for any 400 error to trigger redirect to login
      return {};
    } else if (!response.ok) {
      // For other error status codes, log detailed information but still redirect
      console.error(`Unexpected token refresh error: HTTP ${response.status}. This might require investigation.`);
      captureError(new Error(`Unexpected token refresh HTTP error: ${response.status}`));
      return {};
    }
    
    data = await response.json();
  } catch (err) {
    captureError(err);
    return {}; // Return empty object on error to trigger redirect
  }

  // Only return the data if it contains an access_token
  return data && data.access_token ? data : {};
};

export const verifyMfaFactor = async (
  factorId: string,
  passCode: string,
  requestedOktaScope: OktaScopes
): Promise<any> => {
  const oktaScopeConfig: OktaScopeConfig = OktaScopesConfig[requestedOktaScope];

  if (!oktaScopeConfig) {
    return { error: 'Missing Okta scope configuration' };
  }

  let response, data;
  const stateToken = sessionStorage.getItem(SessionStorageKeys.OKTA_STATE_TOKEN);

  if (!stateToken || !factorId) {
    return { error: 'Missing required MFA verification data' };
  }

  try {
    response = await fetch(`${config.OKTA_DOMAIN}/api/v1/authn/factors/${factorId}/verify`, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        stateToken,
        passCode,
      }),
    });
    data = await response.json();
  } catch (err) {
    captureError(err);
    return { error: 'Failed to verify MFA code' };
  }

  // Clean up state token if we're done with MFA
  if (data.status === 'SUCCESS') {
    sessionStorage.removeItem(SessionStorageKeys.OKTA_STATE_TOKEN);
    // Set a flag to indicate we're redirecting after MFA
    sessionStorage.setItem(SessionStorageKeys.MFA_REDIRECTING, 'true');
  }

  if (!data || !data.sessionToken) {
    if (data) {
      captureError(data);
    }
    return data || { error: 'Invalid verification code' };
  }

  const timeStamp: string = new Date().getTime().toString();

  const codeVerifier: string = generateVerifier();
  sessionStorage.setItem(SessionStorageKeys.OKTA_CODE_VERIFIER, codeVerifier);
  const codeChallenge: string = await computeChallenge(codeVerifier);

  const redirectUrl: string = `${config.OKTA_DOMAIN}/oauth2/${config.OKTA_AUTH_SERVER_ID}/v1/authorize?code_challenge=${codeChallenge}&code_challenge_method=S256&client_id=${config.OKTA_CLIENT_ID}&response_type=code&response_mode=fragment&scope=${oktaScopeConfig.value}offline_access%20openid&redirect_uri=${window.location.origin + PAGES_BASE_PATHNAMES.Login}&state=${timeStamp}&nonce=${timeStamp}&sessionToken=${data.sessionToken}&prompt=none`;

  setTimeout(() => {
    window.location.replace(redirectUrl);
  }, AUTHORIZE_REQUEST_TIMEOUT_DURATION_MS);

  // Return the data so the component knows verification was successful
  return { status: 'SUCCESS', sessionToken: data.sessionToken };
};
